
import { defineComponent, computed, onMounted, ref, onUnmounted, ComponentPublicInstance } from "vue";
import NativeClient, { TitleInfo } from "@/client/native-client";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import store from "@/store";
import router from "@/router";
import ShoppingListRecommendationList from "@/components/shoppingList/ShoppingListRecommendationList.vue";
import ShoppingCategoryList from "@/components/shoppingList/ShoppingCategoryList.vue";
import ShoppingCheckedList from "@/components/shoppingList/ShoppingCheckedList.vue";
import { 
  ShoppingListFoodList, 
  ShoppingListFoodRecommendationsList, 
  ShoppingListRecommendationBannerListItem, 
  ShoppoingListUpdateItem,
  ShoppingListRecommendationParameter,
  ShoppingListRegistListItem
} from "@/types/shoppingList";
import { PantryList } from "@/types/pantry";
import ShoppingListEditMenu from "@/components/shoppingList/ShoppingListEditMenu.vue";
import { translateCategoryList } from "@/utils/shoppingList-util";
import CommonUtil, { isValidApiAccessToken } from "@/utils/common-utils";
import { ApiError } from "@/types/common";

export default defineComponent({
  name: "ShoppingListPage",
  components: {
    ShoppingListRecommendationList,
    ShoppingCategoryList,
    ShoppingCheckedList,
    ShoppingListEditMenu,
    CommonDesignDialog
  },
  setup() {
    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(true, "買い物リスト"));
    CommonUtil.scroll({ top: 0, left: 0 });

    //
    // WebAPIのリクエスト状況（リトライ時に使用する）
    //
    class ShoppingListPageRequestType {
      type: number; // 0:なし、1:一覧取得・更新、2:チェック／チェック解除、3:食材削除、4:食材お気に入り追加、5:チェック食材クリア、6:提案食材追加
      id: number;  // 解雇のリストID（チェック／チェック解除、食材削除、食材お気に入り追加の場合に設定）
      name: string; // 食材名
      check: boolean;  // チェック状態（チェック／チェック解除の場合に設定）
      constructor(type: number, id: number, name: string, check: boolean) {
        this.type = type;
        this.id = id;
        this.name = name;
        this.check = check;
      }
    }
    const refRequestType = ref(new ShoppingListPageRequestType(0, 0, "", false));

    //
    // ダイアログ・タイプ（ボタン選択時に使用する）
    //
    class ShoppingListPageDialogType {
      negativeCallback: (() => void) | null;
      positiveCallback: (() => void) | null;
      constructor(negativeCallback: (() => void) | null, positiveCallback: (() => void) | null) {
        this.negativeCallback = negativeCallback;
        this.positiveCallback = positiveCallback;
      }
    }
    const refDialogType = ref(new ShoppingListPageDialogType(null, null));
    const isShowDesignDialog = ref(false);
    const designDialogType = ref(0);
    const designDialogTitle = ref("");
    const designDialogMessage = ref("");
    const designDialogNegativeButtonLabel = ref("");
    const designDialogPositiveButtonLabel = ref("");
    const designDialogNegativeButtonHandler = () => {
      isShowDesignDialog.value = false;
      if (refDialogType.value.negativeCallback != null) {
        refDialogType.value.negativeCallback();
      }
    };
    const designDialogPositiveButtonHandler = () => {
      isShowDesignDialog.value = false;
      if (refDialogType.value.positiveCallback != null) {
        refDialogType.value.positiveCallback();
      }
    };

    //const { setLogId, outputPageLogs } = useLog();
    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // スクロール
    let startScrollTop = 0;
    const contentsScrollElement = ref<ComponentPublicInstance<HTMLInputElement>>();
    const contentsTouchStart = (e: TouchEvent) => {
      if (contentsScrollElement.value != undefined) {
        NativeClient.sendDebugLog("ShoppingListPage contentsTouchStart  = ");
        startScrollTop = contentsScrollElement.value!.scrollTop;
      }
    }
    const contentsVerticalScroll = (moveY: number) => {
      if (contentsScrollElement.value != undefined && Math.abs(moveY) > 20) {
        //NativeClient.sendDebugLog("ShoppingListPage verticalScroll moveY = " + moveY);
        const topPosition = startScrollTop - moveY;
        contentsScrollElement.value!.scrollTo({ left: 0, top: topPosition })
      }
    };

    //
    // API実行
    //
    // 食品提案のリクエスト
    const getRecommendationList = async (refresh: boolean) => {
      // 提案食材を取得
      const list: ShoppingListFoodList | null = store.getters["shoppingList/getFoodList"];
      if (list != null && list.checkedFoods != null && list.uncheckedFoods != null) {
        const parameter = new ShoppingListRecommendationParameter();
        parameter.refresh = refresh;
        for (const checked of list.checkedFoods) {
          for (const item of checked.items) {
            if (parameter.checkedIds.length >= 64) {
              break;
            }
            if (item.foodId != undefined && item.foodId != null) {
              parameter.checkedIds.push(item.foodId);
            }
          }
        }
        for (const unchecked of list.uncheckedFoods) {
          for (const item of unchecked.items) {
            if (parameter.uncheckedIds.length >= 64) {
              break;
            }
            if (item.foodId != undefined && item.foodId != null) {
              parameter.uncheckedIds.push(item.foodId);
            }
          }
        }
        await store.dispatch("shoppingList/getRecommendationList", parameter);
        NativeClient.sendDebugLog("ShoppingListPage getRecommendationList request complete")
        updateDisplay();
      }
    };
    // 買い物リスト取得（提案食材も一緒に取得する）
    const getShopingListBare = async (refresh: boolean) => {
      refRequestType.value = new ShoppingListPageRequestType(1, 0, "", false);
      NativeClient.sendDebugLog("ShoppingListPage getShopingListBare");
      await store.dispatch("shoppingList/getFoodList"),
      await store.dispatch("shoppingList/getPantryList")
      await getRecommendationList(true);
    };
    const getShoppingList = async () => {
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        await getShopingListBare(true);
        updateDisplay();
      }
      store.dispatch("common/stopLoading");
    };
    // チェック/チェック解除
    const updateCheckFood = async (id: number, check: boolean) => {
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        // フォルダの置き換えAPI
        refRequestType.value = new ShoppingListPageRequestType(2, id, "", check);
        const parameter: ShoppoingListUpdateItem = { id: id, isChecked: check};
        await store.dispatch("shoppingList/updateFoodCheck", parameter);
        // チェック/チェック解除は、レスポンスが買い物リストなので、買い物リストを要求しない
        NativeClient.sendDebugLog("ShoppingListPage updateCheckFood rrequest complete")
        getRecommendationList(true);
      }
      updateDisplay();
      store.dispatch("common/stopLoading");
    };
    // チェック/チェック解除
    const deleteFood = async (id: number) => {
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        // 削除
        refRequestType.value = new ShoppingListPageRequestType(3, id, "", false);
        await store.dispatch("shoppingList/deleteFood", id);

        // 買い物リストが円光されるので要求
        await getShopingListBare(true);
        refRequestType.value = new ShoppingListPageRequestType(1, 0, "", false);
        updateDisplay();
      }
      store.dispatch("common/stopLoading");
    };
    // お気に入りへの追加
    const appendFavorite = async (foodId: number) => {
      NativeClient.sendDebugLog("ShoppingListPage appendFavorite foodId = " + foodId)
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        // 削除
        refRequestType.value = new ShoppingListPageRequestType(3, foodId, "", false);
        await store.dispatch("shoppingList/appendToFavorite", foodId);
      }
      store.dispatch("common/stopLoading");

      // マイページ更新
      NativeClient.requestReloadFavoriteFood();
    };
    // チェック済みをクリア
    const clearCheckedFoodList = async () => {
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        // クリア
        refRequestType.value = new ShoppingListPageRequestType(5, 0, "", false);
        await store.dispatch("shoppingList/clearCheckedItems");

        // 買い物リストを要求
        getShopingListBare(true);
      }
      store.dispatch("common/stopLoading");
    }
    // 提案食材登録
    const registRecommendationFoodErrorCallback = () => {
      getShopingListBare(true); 
    };
    const registRecommendationFood = async (foodId: number, name: string) => {
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      const registName = name == "" ? null : name;
      if (isValidApiAccessToken()) {
        // 登録
        refRequestType.value = new ShoppingListPageRequestType(6, 0, name, false);
        const list: Array<ShoppingListRegistListItem> = [];
        const element = new ShoppingListRegistListItem(foodId, registName);
        NativeClient.sendDebugLog("ShoppingListPage registRecommendationFood element = " + JSON.stringify(element));
        list.push(element);
        await store.dispatch("shoppingList/registShoppingList", list);

        // 追加完了ダイアログ
        refDialogType.value = new ShoppingListPageDialogType(registRecommendationFoodErrorCallback, null);
        designDialogType.value = 13;
        designDialogTitle.value = "食材を追加しました。";
        designDialogMessage.value = "";
        designDialogNegativeButtonLabel.value = "閉じる";
        isShowDesignDialog.value = true;
      }
      store.dispatch("common/stopLoading");
    };

    // メッセージ
    const noWithoutCheckedListMessage = "未チェックの食材がここに表示されます。";
    const noCheckedListMessage = "チェック済みの食材がここに表示されます。";

    // おすすめ食材
    const isExistRecommend = computed(() => {
      const recommendList: ShoppingListFoodRecommendationsList | null = store.getters["shoppingList/getRecommendList"];
      const list: ShoppingListFoodList | null = store.getters["shoppingList/getFoodList"];
      const pantryList: PantryList | null = store.getters["shoppingList/getPantryList"];
      NativeClient.sendDebugLog("ShoppingListPage isExistRecommend recommendList = " + JSON.stringify(recommendList));
      if (recommendList == null || recommendList.foods == null || recommendList.foods.length == 0) {
        return false;
      } else if (list != null && (list.checkedFoods.length > 0 || list.uncheckedFoods.length > 0)) {
        NativeClient.sendDebugLog("ShoppingListPage isExistRecommend shoppingList = " + JSON.stringify(list));
        return true;
      } else if (pantryList == null || pantryList.pantryList.length == 0) {
        NativeClient.sendDebugLog("ShoppingListPage isExistRecommend pantryList no element = " + JSON.stringify(pantryList));
        return false;
      }
      return true;
    });
    const recommendationList = computed(() => {
      const result: Array<ShoppingListRecommendationBannerListItem> = [];
      const recommendList: ShoppingListFoodRecommendationsList = store.getters["shoppingList/getRecommendList"];
      if (recommendList.foods == null) {
        return result;
      }
      for (const item of recommendList.foods) {
        let imageUrl = "";
        const recipeIds: Array<number> = [];
        if (item.reason.contents.length > 0) {
          imageUrl = item.reason.contents[0].image == null ? "" : item.reason.contents[0].image;
          const recipeId = item.reason.contents[0].id == null ? 0 : item.reason.contents[0].id;
          recipeIds.push(recipeId);
        }
        const element: ShoppingListRecommendationBannerListItem = { foodId: item.foodId, imageUrl: imageUrl, title: item.reason.title, description: item.reason.description, name: item.name, recipeIds: recipeIds };
        result.push(element);
      }
      NativeClient.sendDebugLog("ShoppingListPage recommendationList = " + JSON.stringify(result));
      return result;
    });

    // 未チェックの食材リスト
    const uncheckedList = computed(() => {
      NativeClient.sendDebugLog("ShoppingListPage uncheckedList start");
      const source: ShoppingListFoodList = store.getters["shoppingList/getFoodList"];
      if (source == null || source.uncheckedFoods == null) {
        return null;
      } 
      const result = translateCategoryList(source.uncheckedFoods);
      NativeClient.sendDebugLog("ShoppingListPage uncheckedList result = " + JSON.stringify(result));
      return result;
    });

    // チェック済みの食材リスト
    const checkedList = computed(() => {
      NativeClient.sendDebugLog("ShoppingListPage checkedList start");
      const source: ShoppingListFoodList = store.getters["shoppingList/getFoodList"];
      if (source == null || source.checkedFoods == null) {
        return null;
      } 
      const result = source.checkedFoods;
      NativeClient.sendDebugLog("ShoppingListPage checkedList result = " + JSON.stringify(result));
      return result;
    });

    // 本ページの表示／非表示
    let isDisplayed = false;
    onMounted(() => {
      isDisplayed = true;
      getShoppingList();
    });
    onUnmounted(() => {
      isDisplayed = false;
    });

    // 提案食材を登録する
    const clickRecommendation = (foodId: number, foodName: string, recipeId: number) => {
      NativeClient.sendDebugLog("ShoppingListPage clickRecommendation foodId = " + foodId + ", foodName = " + foodName + ", recipeId = " + recipeId);
      registRecommendationFood(foodId, foodName);
    };

    // 提案食材のレシピを表示
    const clickRecommendationRecipe = (recipeId: number) => {
      NativeClient.sendDebugLog("ShoppingListPage clickRecommendationRecipe recipeId = " + recipeId);
      NativeClient.transitionReceipDetail(recipeId, "official")
    };

    // クリア
    const clearCheckedList = () => {
      const source: ShoppingListFoodList = store.getters["shoppingList/getFoodList"];
      if (source == null || source.checkedFoods == null || source.checkedFoods.length == 0) {
        NativeClient.sendDebugLog("ShoppingListPage clearCheckedList not exist");
        return;
      }
      NativeClient.sendDebugLog("ShoppingListPage clearCheckedList exist");
      // クリア確認ダイアログを表示する
      refDialogType.value = new ShoppingListPageDialogType(null, clearCheckedFoodList);
      designDialogType.value = 18;
      designDialogTitle.value = "";
      designDialogMessage.value = "チェック済みの食材をクリアします。\nよろしいですか？";
      designDialogNegativeButtonLabel.value = "キャンセル";
      designDialogPositiveButtonLabel.value = "OK";
      isShowDesignDialog.value = true;
    };

    // 
    const appendIngredientScreen = () => {
      store.dispatch("shoppingList/clearAppendIngredientParameter");
      router.push(`/shoppingListAppendIngredient`);
    };

    // メニュー表示
    const isShowMenu = ref(false);
    const hasFoodId = ref(true);
    const editId = ref(0);
    const editFoodId = ref(0);
    const editClick = (id: number, foodId: number) => {
      NativeClient.sendDebugLog("ShoppingListPage editClick id = " + id + ", foodId = " + foodId);
      if (isShowMenu.value) {
        return;
      }
      editId.value = id;
      if (foodId == undefined || foodId == null) {
        hasFoodId.value = false;
      } else {
        editFoodId.value = foodId;
        hasFoodId.value = true;
      }
      isShowMenu.value = true;
    };
    const deleteFoodFromMenu = () => {
      NativeClient.sendDebugLog("ShoppingListPage deleteFoodFromMenu");
      isShowMenu.value = false;
      deleteFood(editId.value);
    };
    const appendFavoriteFromMenu = () => {
      NativeClient.sendDebugLog("ShoppingListPage appendFavoriteFromMenu editId = " + editId.value);
      isShowMenu.value = false;
      if (editFoodId.value == undefined || editFoodId.value == null) {
        return;
      }
      appendFavorite(editFoodId.value);
    };
    const cancelMenu = () => {
      NativeClient.sendDebugLog("ShoppingListPage cancelMenu");
      isShowMenu.value = false;
    };

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const isShowApiErrorDialog = computed(() => {
      NativeClient.sendDebugLog("ShoppingListPage isShowApiErrorDialog");
      const apiError: ApiError = store.getters["common/getApiError"];
      NativeClient.sendDebugLog("ShoppingListPage isShowApiErrorDialog showApiError");
      if (apiError == null) {
        NativeClient.sendDebugLog("ShoppingListPage isShowApiErrorDialog no error");
        return false;
      }
      NativeClient.sendDebugLog("ShoppingListPage isShowApiErrorDialog error");
      return true;
    });
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    const okApiErrorHandler = () => {
      closeDialog();
      refRequestType.value = new ShoppingListPageRequestType(0, 0, "", false);
    };
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      closeDialog();
      NativeClient.sendDebugLog("ShoppingListPage retryApiErrorHandler");
      // switch (refRequestType.value.type) {
      //   case 1:
      //     getShoppingList();
      //     break;
      //   case 2:
      //     updateCheckFood(refRequestType.value.id, refRequestType.value.check);
      //     break;
      //   case 3:
      //     deleteFood(refRequestType.value.id);
      //     break;
      //   case 4:
      //     appendFavorite(refRequestType.value.id);
      //     break;
      //   case 5:
      //     clearCheckedFoodList();
      //     break
      //   case 6:
      //     registRecommendationFood(refRequestType.value.id, refRequestType.value.name);
      //     break;
      //   default:
      //     break;
      // }
    };

    return {
      updateDisplayKey,
      contentsScrollElement,
      contentsVerticalScroll,
      contentsTouchStart,
      clickRecommendation,
      clickRecommendationRecipe,
      isExistRecommend,
      recommendationList,
      uncheckedList,
      noWithoutCheckedListMessage,
      checkedList,
      noCheckedListMessage,
      appendIngredientScreen,
      isShowMenu,
      hasFoodId,
      updateCheckFood,
      editClick,
      typeApiError,
      handleApiError,
      isShowApiErrorDialog,
      getApiErrorTitle,
      getApiErrorMessage,
      okButtonTitle,
      retryButtonTitle,
      okApiErrorHandler,
      retryApiErrorHandler,
      isShowDesignDialog,
      designDialogType,
      designDialogTitle,
      designDialogMessage,
      designDialogNegativeButtonLabel,
      designDialogPositiveButtonLabel,
      designDialogNegativeButtonHandler,
      designDialogPositiveButtonHandler,
      deleteFoodFromMenu,
      appendFavoriteFromMenu,
      cancelMenu,
      clearCheckedList
    };
  }
});
